// ============================================
// Inter-Entity Ledger
// Computes and displays inter-entity transactions
// from Brand Context configuration
// ============================================

import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, AlertTriangle, Building2 } from 'lucide-react';
import type { BrandConfig } from '../../../types/docops';

// --- Props ---

interface InterEntityLedgerProps {
  monthlyRevenueCents: number;
  brandConfig?: BrandConfig | null;
}

// --- Helpers ---

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

interface LedgerRow {
  from: string;
  to: string;
  type: string;
  amountCents: number;
  basis: string;
}

// --- Component ---

export function InterEntityLedger({
  monthlyRevenueCents,
  brandConfig,
}: InterEntityLedgerProps) {
  // If no brand config, show placeholder
  if (!brandConfig) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <Building2 size={20} className="text-gold" />
          <h3 className="text-lg font-display text-text-primary">
            Inter-Entity Ledger
          </h3>
        </div>
        <div className="text-center py-8">
          <Building2 size={32} className="text-text-muted mx-auto mb-3" />
          <p className="text-base text-text-muted mb-1">
            Configure Brand Context to see inter-entity transactions.
          </p>
          <p className="text-sm text-text-muted">
            The ledger computes royalty payments, retainers, and provider fees
            based on your entity structure.
          </p>
        </div>
      </motion.div>
    );
  }

  // Compute ledger rows from brand config
  const { rows, totalOutflowCents, isComplete } = useMemo(() => {
    const ledgerRows: LedgerRow[] = [];
    let complete = true;

    // 1. IP License Royalty: brandEntity -> trustName
    if (brandConfig.brandEntity && brandConfig.trustName && brandConfig.ipLicenseRoyaltyPercent) {
      const royaltyCents = Math.round(
        (monthlyRevenueCents * brandConfig.ipLicenseRoyaltyPercent) / 100,
      );
      ledgerRows.push({
        from: brandConfig.brandEntity,
        to: brandConfig.trustName,
        type: 'IP License Royalty',
        amountCents: royaltyCents,
        basis: `${brandConfig.ipLicenseRoyaltyPercent}% of revenue`,
      });
    } else {
      complete = false;
    }

    // 2. PC Management Fee: brandEntity -> pcEntityName
    if (brandConfig.brandEntity && brandConfig.pcEntityName && brandConfig.pcRetainer) {
      ledgerRows.push({
        from: brandConfig.brandEntity,
        to: brandConfig.pcEntityName,
        type: 'PC Management Fee',
        amountCents: brandConfig.pcRetainer * 100,
        basis: 'Fixed monthly retainer',
      });
    } else {
      complete = false;
    }

    // 3. Medical Director: pcEntityName -> Provider Network
    if (brandConfig.pcEntityName && brandConfig.mdRetainer) {
      ledgerRows.push({
        from: brandConfig.pcEntityName,
        to: 'Provider Network',
        type: 'Medical Director',
        amountCents: brandConfig.mdRetainer * 100,
        basis: 'Fixed monthly retainer',
      });
    } else {
      complete = false;
    }

    // 4. Provider Consultations: pcEntityName -> Provider Network
    // (consultationCount is passed implicitly through monthlyRevenueCents context,
    //  but we need order data for this — accept it as part of the computation)
    // Note: This row requires order-level data that the parent should provide.
    // For now, we show the row as a line item if consultation fee is configured.
    if (brandConfig.pcEntityName && brandConfig.consultationFee) {
      // The parent passes monthlyRevenueCents but not consultation count.
      // We show a placeholder indicating per-consultation pricing.
      // The actual amount would be: consultationCount * 4900
      // For display, we note the rate.
      ledgerRows.push({
        from: brandConfig.pcEntityName,
        to: 'Provider Network',
        type: 'Provider Consultations',
        amountCents: 0, // Will be filled by parent if count is available
        basis: `$${brandConfig.consultationFee} per consultation`,
      });
    }

    const outflow = ledgerRows
      .filter((r) => r.from === brandConfig.brandEntity)
      .reduce((sum, r) => sum + r.amountCents, 0);

    return { rows: ledgerRows, totalOutflowCents: outflow, isComplete: complete };
  }, [monthlyRevenueCents, brandConfig]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="bg-bg-card border border-bg-tertiary rounded-xl p-5 mb-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <Building2 size={20} className="text-gold" />
        <h3 className="text-lg font-display text-text-primary">
          Inter-Entity Ledger
        </h3>
      </div>

      {rows.length === 0 ? (
        <p className="text-text-muted text-base">
          No inter-entity transactions to display. Check brand configuration.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead>
              <tr className="border-b border-bg-tertiary">
                <th className="py-2 pr-3 text-sm font-medium text-text-muted uppercase tracking-wider text-left">
                  From
                </th>
                <th className="py-2 pr-3 text-sm font-medium text-text-muted uppercase tracking-wider text-left">
                  To
                </th>
                <th className="py-2 pr-3 text-sm font-medium text-text-muted uppercase tracking-wider text-left">
                  Type
                </th>
                <th className="py-2 pr-3 text-sm font-medium text-text-muted uppercase tracking-wider text-right">
                  Amount
                </th>
                <th className="py-2 text-sm font-medium text-text-muted uppercase tracking-wider text-left">
                  Basis
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={`${row.from}-${row.type}-${i}`}
                  className="border-b border-bg-tertiary last:border-0"
                >
                  <td className="py-3 pr-3 text-text-primary">
                    <span className="truncate block max-w-[180px]" title={row.from}>
                      {row.from}
                    </span>
                  </td>
                  <td className="py-3 pr-3 text-text-secondary">
                    <div className="flex items-center gap-1.5">
                      <ArrowRight size={14} className="text-text-muted flex-shrink-0" />
                      <span className="truncate max-w-[180px]" title={row.to}>
                        {row.to}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 pr-3 text-text-secondary">{row.type}</td>
                  <td className="py-3 pr-3 text-text-primary font-medium text-right tabular-nums whitespace-nowrap">
                    {row.amountCents > 0 ? formatCurrency(row.amountCents) : '--'}
                  </td>
                  <td className="py-3 text-text-muted text-sm">{row.basis}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Balance check */}
          <div className="mt-4 pt-3 border-t border-bg-tertiary flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-base text-text-secondary font-medium">
                Balance Check:
              </span>
              {isComplete ? (
                <span className="flex items-center gap-1.5 text-optimal text-base font-medium">
                  <CheckCircle2 size={16} />
                  CLEAN
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-suboptimal text-base font-medium">
                  <AlertTriangle size={16} />
                  Missing data
                </span>
              )}
            </div>
            <span className="text-base text-text-muted tabular-nums">
              Primary entity outflow: {formatCurrency(totalOutflowCents)}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
}
